import { useCallback, useEffect, useRef, useState } from "react";
import { useLocation, useNavigate, useParams } from "react-router-dom";
import { toast } from "react-hot-toast";
import { ArrowLeftIcon, ChatBubbleLeftRightIcon, ArrowPathIcon, PencilSquareIcon, UserPlusIcon, TrashIcon } from "@heroicons/react/24/outline";
import cn from "classnames";
import Button from "../common/Button";
import SlideDrawer from "../common/SlideDrawer";
import Tooltip from "../common/Tooltip";
import { useLocalStorageState } from "../../hooks/useLocalStorageState";
import { api, CallListItem, downloadFile, InvestigationDetail as Detail, InvestigationTabKey } from "./api";
import { CLEAR_MS, clearingClass, describeUpdates } from "./updates";
import InvestigationChat from "./InvestigationChat";
import NewInvestigationModal from "./NewInvestigationModal";
import MembersModal from "./MembersModal";
import ReanalyzeModal from "./ReanalyzeModal";
import DraftReportModal from "./DraftReportModal";
import ExportMenu, { ExportAction } from "./ExportMenu";
import ExportTranscriptModal from "./ExportTranscriptModal";
import ExportSummaryModal from "./ExportSummaryModal";
import ExportLeadsModal from "./ExportLeadsModal";
import ExportTimelineModal from "./ExportTimelineModal";
import ExportDiscoveryModal from "./ExportDiscoveryModal";
import DeleteInvestigationModal from "./DeleteInvestigationModal";
import BriefTab from "./tabs/BriefTab";
import LeadsTab from "./tabs/LeadsTab";
import CallsTab from "./tabs/CallsTab";
import FilesTab from "./tabs/FilesTab";
import PeopleTab from "./tabs/PeopleTab";
import TimelineTab from "./tabs/TimelineTab";
import { useStaffView } from "./StaffView";

// Files first: nothing else exists until something is uploaded, and the
// other tabs stay disabled until then.
const TABS = [
  { key: "files", label: "Files" },
  { key: "chat", label: "Chat" },
  { key: "brief", label: "Brief" },
  { key: "leads", label: "Leads" },
  { key: "calls", label: "Calls" },
  { key: "people", label: "Mentions" },
  { key: "timeline", label: "Timeline" },
] as const;
type TabKey = (typeof TABS)[number]["key"];
const isUpdateTab = (t: TabKey): t is InvestigationTabKey => t === "brief" || t === "leads" || t === "calls" || t === "people" || t === "timeline";

interface Props {
  onListChanged: () => Promise<void>;
}

const InvestigationDetail: React.FC<Props> = ({ onListChanged }) => {
  const { investigationId = "", tab } = useParams();
  const navigate = useNavigate();
  const location = useLocation();
  // Staff portal view: read-only, so every control that writes or exports is hidden.
  const staffView = Boolean(useStaffView());
  // Arrived here from inside a call or file detail (a mention chip, for
  // instance): offer a real history back so the detective lands on what
  // they were reading.
  const cameFromDetail = (location.state?.back === "call" || location.state?.back === "file") && location.key !== "default";
  const backLabel = location.state?.back === "file" ? "Back to file" : "Back to call";
  const [detail, setDetail] = useState<Detail | null>(null);
  const [loading, setLoading] = useState(true);
  const [chatOpen, setChatOpen] = useLocalStorageState("investigations.chatOpen", true);
  const [editing, setEditing] = useState(false);
  const [addingMember, setAddingMember] = useState(false);
  const [confirmReanalyze, setConfirmReanalyze] = useState(false);
  const [draftReportOpen, setDraftReportOpen] = useState(false);
  const [confirmDelete, setConfirmDelete] = useState(false);
  // Export menu: which download is in flight, and the transcript picker's
  // call list (fetched when the picker opens, so the header never pays for
  // it otherwise).
  const [exporting, setExporting] = useState<ExportAction | null>(null);
  const [transcriptsOpen, setTranscriptsOpen] = useState(false);
  const [summaryOpen, setSummaryOpen] = useState(false);
  const [leadsOpen, setLeadsOpen] = useState(false);
  const [timelineOpen, setTimelineOpen] = useState(false);
  const [discoveryOpen, setDiscoveryOpen] = useState(false);
  const [exportCalls, setExportCalls] = useState<CallListItem[] | null>(null);
  const runExport = async (action: ExportAction) => {
    if (action === "draft") return setDraftReportOpen(true);
    if (action === "summary") return setSummaryOpen(true);
    if (action === "leads") return setLeadsOpen(true);
    if (action === "timeline") return setTimelineOpen(true);
    if (action === "discovery") return setDiscoveryOpen(true);
    if (action === "transcripts") {
      setTranscriptsOpen(true);
      setExportCalls(null);
      api.calls(investigationId).then(setExportCalls).catch(() => toast.error("Could not load the calls"));
      return;
    }
  };
  const runDiscovery = async (format: "pdf" | "docx") => {
    setExporting("discovery");
    const slow = toast.loading("Building the discovery package…");
    try {
      await downloadFile(api.exportUrl(investigationId, format), "discovery.zip");
      toast.success("Discovery package downloaded", { id: slow });
    } catch (e: any) {
      toast.dismiss(slow);
      toast.error(e.message || "Export failed");
      throw e;
    } finally {
      setExporting(null);
    }
  };
  // Optimistic: flip to the analyzing state on request, then defer to the
  // server's job count once the poll picks it up (20s safety valve).
  const [reanalyzeRequested, setReanalyzeRequested] = useState(false);
  useEffect(() => {
    if (!reanalyzeRequested) return;
    const t = setTimeout(() => setReanalyzeRequested(false), 20000);
    return () => clearTimeout(t);
  }, [reanalyzeRequested]);
  // The main pane is its own scroll container, so the browser won't restore
  // its position on back navigation. Remember it per investigation+tab and
  // restore once the tab's content is tall enough to scroll there.
  const scrollRef = useRef<HTMLDivElement>(null);
  const scrollKey = `inv.scroll.${investigationId}.${tab || ""}`;
  const onPaneScroll = () => {
    const el = scrollRef.current;
    if (el) sessionStorage.setItem(scrollKey, String(el.scrollTop));
  };
  useEffect(() => {
    const el = scrollRef.current;
    if (!el) return;
    const saved = Number(sessionStorage.getItem(scrollKey) || 0);
    if (!saved) return;
    let tries = 0;
    const attempt = () => {
      if (!scrollRef.current) return;
      const target = scrollRef.current;
      if (target.scrollHeight - target.clientHeight >= saved || tries >= 20) {
        target.scrollTop = saved;
        return;
      }
      tries++;
      setTimeout(attempt, 100);
    };
    attempt();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [scrollKey, detail?.id]);
  const hasFiles = (detail?.counts.files ?? 0) > 0;
  const requestedTab: TabKey | null = TABS.some((t) => t.key === tab) ? (tab as TabKey) : null;
  // No files yet → everything but Files is disabled and we land on Files.
  const activeTab: TabKey = !detail ? requestedTab || "files" : !hasFiles ? "files" : requestedTab || "chat";

  const load = useCallback(async () => {
    try {
      const d = await api.get(investigationId);
      setDetail(d);
    } catch {
      toast.error("Investigation not found");
      navigate("/investigations");
    } finally {
      setLoading(false);
    }
  }, [investigationId, navigate]);

  // Clear a tab's "new since" marker. The highlights ease off first
  // (`clearing` drives the transitions), then the stamp moves and a reload
  // drops the markers for real.
  const [clearing, setClearing] = useState<InvestigationTabKey | null>(null);
  const markSeen = useCallback(
    async (tab: InvestigationTabKey) => {
      // Staff don't have a membership to stamp.
      if (staffView) return;
      setClearing(tab);
      try {
        await Promise.all([api.markSeen(investigationId, tab), new Promise((r) => setTimeout(r, CLEAR_MS))]);
        await load();
      } catch {
      } finally {
        setClearing(null);
      }
    },
    [investigationId, load, staffView]
  );

  useEffect(() => {
    load();
  }, [load]);

  // Keep the URL honest when a disabled tab is requested (bookmark, back
  // button) before any files exist.
  useEffect(() => {
    if (detail && !hasFiles && requestedTab && requestedTab !== "files") {
      navigate(`/investigations/${investigationId}/files`, { replace: true });
    }
  }, [detail, hasFiles, requestedTab, investigationId, navigate]);

  const serverReanalyzing = (detail?.counts.reanalyzing ?? 0) > 0;
  useEffect(() => {
    if (serverReanalyzing) setReanalyzeRequested(false);
  }, [serverReanalyzing]);
  const reanalyzing = reanalyzeRequested || serverReanalyzing;

  useEffect(() => {
    if (!detail || (detail.counts.processing === 0 && !detail.counts.rollupRunning && !reanalyzing)) return;
    const t = setInterval(load, detail.counts.rollupRunning || reanalyzing ? 5000 : 8000);
    return () => clearInterval(t);
  }, [detail, load, reanalyzing]);

  const startReanalyze = async () => {
    setReanalyzeRequested(true);
    try {
      await api.reanalyze(investigationId);
      await load();
    } catch {
      setReanalyzeRequested(false);
      toast.error("Could not start re-analysis");
      throw new Error("reanalyze failed");
    }
  };

  if (loading || !detail) {
    return (
      <div className="flex-1 overflow-auto p-6">
        <div className="animate-pulse space-y-4">
          <div className="h-8 bg-base-300 rounded w-1/3" />
          <div className="h-48 bg-base-300 rounded" />
        </div>
      </div>
    );
  }

  const tabContent = {
    chat: <InvestigationChat investigationId={investigationId} investigationName={detail.name} />,
    brief: <BriefTab detail={detail} onRefresh={load} reanalyzing={reanalyzing} onSeen={() => markSeen("brief")} clearing={clearing === "brief"} />,
    leads: <LeadsTab investigationId={investigationId} reanalyzing={reanalyzing} updates={detail.updates.leads} since={detail.seenAt.leads} onSeen={() => markSeen("leads")} clearing={clearing === "leads"} />,
    calls: <CallsTab investigationId={investigationId} processing={detail.counts.processing} reanalyzing={reanalyzing} updates={detail.updates.calls} since={detail.seenAt.calls} onSeen={() => markSeen("calls")} clearing={clearing === "calls"} />,
    files: <FilesTab investigationId={investigationId} detail={detail} onRefresh={load} onListChanged={onListChanged} />,
    people: <PeopleTab investigationId={investigationId} reanalyzing={reanalyzing} updates={detail.updates.people} since={detail.seenAt.people} onSeen={() => markSeen("people")} clearing={clearing === "people"} />,
    timeline: <TimelineTab investigationId={investigationId} detail={detail} reanalyzing={reanalyzing} updates={detail.updates.timeline} since={detail.seenAt.timeline} onSeen={() => markSeen("timeline")} clearing={clearing === "timeline"} onRefresh={load} />,
  }[activeTab];

  return (
    <div className="flex-1 flex min-h-0">
      <div className="flex-1 flex flex-col min-w-0">
        <div className="px-6 pt-4 pb-0 border-b border-base-300">
          <div className="flex items-start gap-3 flex-wrap">
            {cameFromDetail ? (
              <Button variant="secondary" size="sm" className="mt-0.5" onClick={() => navigate(-1)} title="Back to what you were reading">
                <ArrowLeftIcon className="h-4 w-4 mr-1" /> {backLabel}
              </Button>
            ) : staffView ? (
              <Button variant="secondary" size="sm" className="mt-0.5" onClick={() => navigate("/agency-management")} title="Back to the staff portal">
                <ArrowLeftIcon className="h-4 w-4 mr-1" /> Staff portal
              </Button>
            ) : (
              <Button variant="secondary" size="sm" className="mt-0.5" onClick={() => navigate("/investigations")} title="All investigations">
                <ArrowLeftIcon className="h-4 w-4 mr-1" /> Investigations
              </Button>
            )}
            <div className="min-w-0 flex-1">
              <h1 className="text-xl font-bold text-base-content truncate flex items-center gap-2">
                {detail.name}
                {!staffView && (
                <Button variant="secondary" size="xs" className="px-1.5" onClick={() => setEditing(true)} title="Edit">
                  <PencilSquareIcon className="size-4" />
                </Button>
                )}
              </h1>
              <p className="text-xs text-base-content/60">
                {detail.case_number ? `Case ${detail.case_number} · ` : ""}
                {detail.counts.files} files
                {detail.counts.processing > 0 && (
                  <span className="text-amber-700 dark:text-amber-400"> · {detail.counts.processing} processing</span>
                )}
                {detail.counts.failed > 0 && <span className="text-red-600 dark:text-red-400"> · {detail.counts.failed} failed</span>}
                {" · "}
                {detail.members.length} member{detail.members.length === 1 ? "" : "s"}
                {!staffView && (
                  <button className="ml-1 text-blue-600 dark:text-blue-300 hover:underline" onClick={() => setAddingMember(true)}>
                    <UserPlusIcon className="size-3.5 inline" /> manage
                  </button>
                )}
              </p>
            </div>
            <div className="flex items-center gap-1">
            {!staffView && <ExportMenu hasFiles={hasFiles} busy={exporting} onAction={runExport} />}
            {detail.role === "owner" && !staffView && (
              <Button
                variant="secondary"
                size="sm"
                className="px-2"
                title={reanalyzing ? "Re-analysis in progress" : "Re-run analysis on every call"}
                disabled={reanalyzing}
                onClick={() => setConfirmReanalyze(true)}
              >
                <ArrowPathIcon className={cn("size-4", reanalyzing && "animate-spin")} />
              </Button>
            )}
            {detail.role === "owner" && !staffView && (
              <Button variant="secondary" size="sm" className="px-2 hover:text-red-600 dark:hover:text-red-400" title="Delete investigation" onClick={() => setConfirmDelete(true)}>
                <TrashIcon className="size-4" />
              </Button>
            )}
            {activeTab !== "chat" && hasFiles && (
              <Button variant={chatOpen ? "primary" : "secondary"} size="sm" onClick={() => setChatOpen(!chatOpen)}>
                <ChatBubbleLeftRightIcon className="h-4 w-4 mr-1" /> Ask
              </Button>
            )}
            </div>
          </div>
          {!detail.privilege_attested_at && detail.counts.files > 0 && (
            <div className="mt-2 text-xs rounded-md bg-amber-50 dark:bg-amber-950/40 text-amber-800 dark:text-amber-400 px-3 py-1.5">
              Uploads require an attorney-client privilege attestation. You'll be asked before the first upload.
            </div>
          )}
          <nav className="mt-3 flex gap-0 -mb-px overflow-x-auto">
            {TABS.map((t) => {
              const disabled = !hasFiles && t.key !== "files";
              const pending = isUpdateTab(t.key) && !disabled ? detail.updates[t.key] : 0;
              const button = (
              <button
                key={t.key}
                disabled={disabled}
                title={disabled ? "Add files first" : undefined}
                onClick={() => navigate(`/investigations/${investigationId}/${t.key}`)}
                className={cn(
                  "relative px-4 py-2 text-sm font-medium whitespace-nowrap border-b-2 transition-colors",
                  activeTab === t.key
                    ? "border-blue-500 text-blue-600 dark:text-blue-300"
                    : disabled
                      ? "border-transparent text-base-content/30 cursor-not-allowed"
                      : "border-transparent text-base-content/60 hover:text-base-content/80 hover:border-base-content/30"
                )}
              >
                {t.label}
                {t.key === "leads" && detail.counts.newLeads > 0 && (
                  <span className="ml-1.5 text-[11px] px-1.5 rounded-full bg-red-100 text-red-800 dark:bg-red-950/40 dark:text-red-400">{detail.counts.newLeads}</span>
                )}
                {pending > 0 && <span className={cn("absolute top-1.5 right-1.5 size-2 rounded-full bg-blue-400 dark:bg-blue-300 ring-2 ring-base-100", clearingClass(clearing === t.key))} aria-label={describeUpdates(t.key as InvestigationTabKey, pending)} />}
              </button>
              );
              return pending > 0 ? (
                <Tooltip key={t.key} placement="bottom" overlay={describeUpdates(t.key as InvestigationTabKey, pending, detail.seenAt[t.key as InvestigationTabKey])}>
                  {button}
                </Tooltip>
              ) : (
                button
              );
            })}
          </nav>
        </div>
        {activeTab === "chat" ? (
          <div className="flex-1 min-h-0">{tabContent}</div>
        ) : (
          <div ref={scrollRef} onScroll={onPaneScroll} className="flex-1 overflow-auto">
            <div className={cn("mx-auto w-full px-6 py-8", activeTab === "brief" ? "max-w-4xl" : "max-w-6xl")}>{tabContent}</div>
          </div>
        )}
      </div>

      <SlideDrawer open={chatOpen && activeTab !== "chat" && hasFiles} storageKey="pnai-investigationChatWidth" className="border-l border-base-300 bg-base-100">
        <InvestigationChat investigationId={investigationId} investigationName={detail.name} compact onClose={() => setChatOpen(false)} />
      </SlideDrawer>

      <NewInvestigationModal
        open={editing}
        investigation={detail}
        onClose={() => setEditing(false)}
        onSaved={async (values) => {
          await api.update(investigationId, values);
          setEditing(false);
          await load();
          await onListChanged();
        }}
        onSaveAndReanalyze={async (values) => {
          await api.update(investigationId, values);
          setEditing(false);
          await startReanalyze();
          await onListChanged();
          toast.success("Saved. Re-analyzing every call.");
        }}
      />

      <ReanalyzeModal
        open={confirmReanalyze}
        callCount={detail.counts.calls}
        onClose={() => setConfirmReanalyze(false)}
        onConfirm={async () => {
          await startReanalyze();
          setConfirmReanalyze(false);
          toast.success("Re-analyzing every call");
        }}
      />

      <MembersModal open={addingMember} onClose={() => setAddingMember(false)} detail={detail} onChanged={load} />
      <DraftReportModal open={draftReportOpen} onClose={() => setDraftReportOpen(false)} investigationId={investigationId} investigationName={detail.case_number || detail.name} />
      <ExportSummaryModal open={summaryOpen} onClose={() => setSummaryOpen(false)} investigationId={investigationId} />
      <ExportLeadsModal open={leadsOpen} onClose={() => setLeadsOpen(false)} investigationId={investigationId} />
      <ExportDiscoveryModal open={discoveryOpen} onClose={() => setDiscoveryOpen(false)} onExport={runDiscovery} />
      <ExportTimelineModal
        open={timelineOpen}
        onClose={() => setTimelineOpen(false)}
        investigationId={investigationId}
        hiddenCount={Object.values((detail.timeline_overrides as Record<string, { hidden?: boolean }> | null) || {}).filter((o) => o.hidden).length}
      />
      <ExportTranscriptModal
        open={transcriptsOpen}
        onClose={() => setTranscriptsOpen(false)}
        investigationId={investigationId}
        defaultCaseId={detail.case_number || investigationId}
        calls={exportCalls ?? undefined}
        callsLoading={transcriptsOpen && exportCalls === null}
      />

      <DeleteInvestigationModal
        investigation={confirmDelete ? { id: detail.id, name: detail.name, fileCount: detail.counts.files } : null}
        onClose={() => setConfirmDelete(false)}
        onConfirm={async () => {
          try {
            await api.remove(detail.id);
            toast.success("Investigation deleted");
            await onListChanged();
            navigate("/investigations");
          } catch (e: any) {
            toast.error(e?.response?.data?.error || "Could not delete investigation");
          }
        }}
      />
    </div>
  );
};

export default InvestigationDetail;
